import React, { useState, useEffect, useContext, useMemo } from "react";
import { bibleDownloader } from "storage/BibleDownloader/BibleDownloader";
import { useBible } from "components/Bible/BibleContainer";

interface DownloadsState {
  [bibleId: string]: {
    [key: string]: any;
  };
}

interface BibleDownloadsCtx {
  downloads: DownloadsState;
  handleDownload: (bibleId: string) => () => void;
  handleDelete: (bibleId: string) => () => void;
  offlineBibleIds: string[];
}

export const BibleDownloadsContext = React.createContext({
  downloads: {},
  handleDownload: () => () => {},
  handleDelete: () => () => {},
  offlineBibleIds: []
} as BibleDownloadsCtx);

export const BibleDownloadsProvider = ({ children }: { children: any }) => {
  const { bibleIds } = useBible();
  const [downloads, setDownloads] = useState<DownloadsState>({});

  // NOTE: calculates the offlineBibleIds in a ways that's easily memoized
  // so that offlineBibleIds can be used as a dependency in children's
  // hook depedencies
  const offlineBibleIdsString = Object.entries(downloads).reduce(
    (acc, [bibleId, downloadState]) => {
      if (downloadState.progress === 100) acc = acc + "," + bibleId;
      return acc;
    },
    ""
  );
  const offlineBibleIds = useMemo(() => {
    return offlineBibleIdsString.split(",");
  }, [offlineBibleIdsString]);

  const updateDownloads = (updatesByBibleId: DownloadsState) =>
    setDownloads(prevDownloads => {
      const updates: DownloadsState = {};
      Object.keys(updatesByBibleId).forEach(bibleId => {
        updates[bibleId] = {
          ...prevDownloads[bibleId],
          ...updatesByBibleId[bibleId]
        };
      });
      return { ...prevDownloads, ...updates };
    });

  const updateDownload = (bibleId: string, update: { [key: string]: any }) =>
    updateDownloads({ [bibleId]: update });

  // get download progress (async calculation) for each bible
  useEffect(() => {
    async function setDownloadProgresses() {
      let progressByBibleId: DownloadsState = {};
      for (let bibleId of bibleIds) {
        const progress = await bibleDownloader.getDownloadProgress(bibleId);
        progressByBibleId[bibleId] = { progress };
      }
      updateDownloads(progressByBibleId);
    }
    setDownloadProgresses();
  }, [bibleIds]);

  const handleDownload = (bibleId: string) => () => {
    const abort = bibleDownloader.downloadBible(bibleId, progress =>
      updateDownload(bibleId, { progress })
    );
    updateDownload(bibleId, { abort });
  };

  const handleDelete = (bibleId: string) => () => {
    // HACK: we set the download to no progress before the deletion has happened
    // since chrome can take up to 30 seconds to delete a bible due to slow IndexedDB
    updateDownload(bibleId, { progress: undefined });
    bibleDownloader.deleteBible(bibleId).then(() => {
      updateDownload(bibleId, { progress: undefined });
    });
  };

  return (
    <BibleDownloadsContext.Provider
      value={{ downloads, handleDelete, handleDownload, offlineBibleIds }}
    >
      {children}
    </BibleDownloadsContext.Provider>
  );
};

export const useBibleDownloads = () => useContext(BibleDownloadsContext);
